
import { useState, useCallback, useEffect, useMemo } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'
import { Shield } from 'lucide-react'
import Cookies from 'js-cookie'
import { formatPhone } from '@/shared/utils/formatters/phoneUtils'

const MARITAL_OPTIONS = [
    { value: '1', label: 'No' },
    { value: '2', label: 'Yes "Married File Separate"' },
    { value: '3', label: 'Yes "Married File Joint"' }
]

const STATE_TO_CODE: Record<string, string> = {
    "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
    "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "District of Columbia": "DC",
    "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL",
    "Indiana": "IN", "Iowa": "IA", "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA",
    "Maine": "ME", "Maryland": "MD", "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN",
    "Mississippi": "MS", "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV",
    "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
    "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", "Oregon": "OR",
    "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD",
    "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT", "Virginia": "VA",
    "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY"
}

// Spouse age eligibility constants
const SPOUSE_MIN_AGE = 19
const SPOUSE_MAX_AGE = 64

// Dependent age eligibility constant (must be younger than 23)
const DEPENDENT_MAX_AGE = 22

interface Dependent {
    id: string
    firstName: string
    lastName: string
    dob: string
    ssn: string
    onPolicy: string
}

interface EnrollmentDataStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    isMasterMode?: boolean
    [key: string]: any
}

export default function EnrollmentDataStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    isMasterMode = false,
    totalSteps = 6,
    revealAllFields = false
}: EnrollmentDataStepProps) {
    // ✅ DEBUG: Log revealAllFields state
    useEffect(() => {
        if (revealAllFields) {
            console.log('🛡️ [EnrollmentDataStep] REVEAL ALL FIELDS ACTIVE');
        }
    }, [revealAllFields]);

    const [errors, setErrors] = useState<ValidationErrors>({})
    const [activeDependent, setActiveDependent] = useState<Dependent | null>(null)
    const [isEditingDependent, setIsEditingDependent] = useState(false)
    const [dependentFormErrors, setDependentFormErrors] = useState<{ [key: string]: string }>({})

    const hasDependents = enrollmentData.hasDependents
    const dependents: Dependent[] = enrollmentData.dependentsList || []
    const maritalStatus = enrollmentData.maritalStatus

    // Real-time phone validation
    const phoneDigits = useMemo(() => (enrollmentData.clientPhone || '').replace(/\D/g, ''), [enrollmentData.clientPhone])
    const isPhoneValid = phoneDigits.length === 10
    const showPhoneError = phoneDigits.length > 0 && phoneDigits.length < 10

    // Real-time email validation
    const emailValue = enrollmentData.clientEmail || ''
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isEmailValid = emailRegex.test(emailValue)
    const showEmailError = emailValue.length > 0 && !isEmailValid

    // Real-time mailing zip code validation
    const mailingZipValue = enrollmentData.mailingZip || ''
    const zipRegex = /^\d{5}(-\d{4})?$/
    const isMailingZipValid = zipRegex.test(mailingZipValue)
    const showMailingZipError = mailingZipValue.length > 0 && !isMailingZipValid

    // Calculate age from DOB
    const calculateAge = useCallback((dob: string): number | null => {
        if (!dob) return null
        const birthDate = new Date(dob)
        const today = new Date()
        let age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--
        }
        return age
    }, [])

    // Real-time spouse DOB validation
    const campaignConfig = useMemo(() => enrollmentData.campaign || {}, [enrollmentData.campaign])
    const minAge = campaignConfig.minAge || SPOUSE_MIN_AGE
    const maxAge = campaignConfig.maxAge || SPOUSE_MAX_AGE

    const spouseAge = useMemo(() => calculateAge(enrollmentData.spouseDOB), [calculateAge, enrollmentData.spouseDOB])
    const isSpouseAgeValid = spouseAge === null || (spouseAge >= minAge && spouseAge <= maxAge)
    const showSpouseAgeError = enrollmentData.spouseDOB && !isSpouseAgeValid

    // Real-time dependent DOB validation
    const activeDependentAge = useMemo(() => activeDependent ? calculateAge(activeDependent.dob) : null, [calculateAge, activeDependent])
    const isDependentAgeValid = activeDependentAge === null || activeDependentAge <= DEPENDENT_MAX_AGE
    const showDependentAgeError = activeDependent?.dob && !isDependentAgeValid

    useEffect(() => {
        let count = 1 // Client

        // Add spouse if married file joint (value '3')
        if (maritalStatus === '3') {
            count += 1
        }

        // Add dependents ONLY if they are on the policy
        if (enrollmentData.dependentsList && Array.isArray(enrollmentData.dependentsList)) {
            const onPolicyCount = enrollmentData.dependentsList.filter((dep: any) => dep.onPolicy === 'Yes').length
            count += onPolicyCount
        }

        if (enrollmentData.householdSize !== count) {
            updateEnrollmentData('householdSize', count)
        }
    }, [maritalStatus, enrollmentData.dependentsList, updateEnrollmentData, enrollmentData.householdSize])

    // Auto-set isPregnant to "No" whenever clientSex changes to Female (question stays hidden for agents)
    useEffect(() => {
        if (enrollmentData.clientSex === 'Female' && !enrollmentData.isPregnant) {
            updateEnrollmentData('isPregnant', 'No')
        }
    }, [enrollmentData.clientSex, enrollmentData.isPregnant, updateEnrollmentData])

    // Set default values for smoker, pregnant, and mailing address
    useEffect(() => {
        if (enrollmentData.isSmoker === undefined || enrollmentData.isSmoker === null) {
            updateEnrollmentData('isSmoker', 'No')
        }
        if (enrollmentData.hasSeparateMailingAddress === undefined || enrollmentData.hasSeparateMailingAddress === null) {
            updateEnrollmentData('hasSeparateMailingAddress', 'No')
        }

        // SECTION 2: Optimization - Set defaults for new enrollments
        // City: Clear specifically if it contains "MOCK CITY" placeholder
        if (enrollmentData.clientCity?.toUpperCase() === 'MOCK CITY') {
            updateEnrollmentData('clientCity', '')
        }

        // Married: Autofill to "NO" (Code "1" matches "No/Single")
        if (!enrollmentData.maritalStatus) {
            updateEnrollmentData('maritalStatus', '1')
        }

        // Dependents: Autofill to "NO"
        if (!enrollmentData.hasDependents) {
            updateEnrollmentData('hasDependents', 'No')
        }
    }, [enrollmentData.clientCity, enrollmentData.maritalStatus, enrollmentData.hasDependents, updateEnrollmentData])

    const handleNext = useCallback(async (): Promise<boolean> => {
        const validationErrors: ValidationErrors = {}

        // Enrollment data validation
        if (!enrollmentData.clientAddress) validationErrors.clientAddress = 'Address is required'
        if (!enrollmentData.clientCity) validationErrors.clientCity = 'City is required'
        if (!enrollmentData.clientPhone) validationErrors.clientPhone = 'Phone is required'
        if (!enrollmentData.clientEmail) validationErrors.clientEmail = 'Email is required'
        if (!enrollmentData.clientSex) validationErrors.clientSex = 'Sex is required'
        if (!enrollmentData.isSmoker) validationErrors.isSmoker = 'Smoker status is required'
        if (enrollmentData.clientSex === 'Female' && !enrollmentData.isPregnant) {
            validationErrors.isPregnant = 'Pregnancy status is required'
        }

        // Mailing address validation (only when separate mailing address is selected)
        if (enrollmentData.hasSeparateMailingAddress === 'Yes') {
            if (!enrollmentData.mailingAddress) validationErrors.mailingAddress = 'Mailing address is required'
            if (!enrollmentData.mailingCity) validationErrors.mailingCity = 'Mailing city is required'
            if (!enrollmentData.mailingState) validationErrors.mailingState = 'Mailing state is required'
            if (!enrollmentData.mailingZip) {
                validationErrors.mailingZip = 'Mailing zip code is required'
            } else if (!/^\d{5}(-\d{4})?$/.test(enrollmentData.mailingZip)) {
                validationErrors.mailingZip = 'Please enter a valid zip code (e.g., 12345 or 12345-6789)'
            }
        }

        // Spousal data validation
        if (!maritalStatus) {
            validationErrors.maritalStatus = 'Please select marital status'
        } else if (maritalStatus === '3' || (maritalStatus === '2' && enrollmentData.spouseFirstName) || (isMasterMode && revealAllFields)) {
            if (!enrollmentData.spouseFirstName) validationErrors.spouseFirstName = 'Spouse first name is required'
            if (!enrollmentData.spouseLastName) validationErrors.spouseLastName = 'Spouse last name is required'
            if (!enrollmentData.spouseDOB) {
                validationErrors.spouseDOB = 'Spouse date of birth is required'
            } else {
                const spouseAgeVal = calculateAge(enrollmentData.spouseDOB)
                if (spouseAgeVal !== null && (spouseAgeVal < minAge || spouseAgeVal > maxAge)) {
                    if (spouseAgeVal < minAge) {
                        validationErrors.spouseDOB = `Too young: Spouse must be at least ${minAge} years old`
                    } else {
                        validationErrors.spouseDOB = `Too old: Spouse must be ${maxAge} years old or younger`
                    }
                }
            }
            if (!enrollmentData.spouseSSN) {
                validationErrors.spouseSSN = 'Spouse SSN is required'
            } else if (enrollmentData.spouseSSN.replace(/\D/g, '').length !== 9) {
                validationErrors.spouseSSN = 'Spouse SSN must be 9 digits'
            }
        }

        // Dependents validation
        if (!hasDependents) {
            validationErrors.hasDependents = 'Please select dependent status'
        } else if (hasDependents === 'Yes "I File Them On My Taxes"' && dependents.length === 0 && !isEditingDependent) {
            validationErrors.hasDependents = 'Please add at least one dependent or finish editing'
        }

        setErrors(validationErrors)
        return Object.keys(validationErrors).length === 0
    }, [enrollmentData, maritalStatus, hasDependents, dependents, isEditingDependent, calculateAge, revealAllFields])

    const handlePhoneChange = (val: string) => {
        updateEnrollmentData('clientPhone', formatPhone(val))
    }

    const handleSpouseSSNChange = (val: string) => {
        const nums = val.replace(/\D/g, '').substring(0, 9)
        let formatted = nums
        if (nums.length > 5) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}-${nums.substring(5, 9)}`
        } else if (nums.length > 3) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}`
        }
        updateEnrollmentData('spouseSSN', formatted)
    }

    const handleDependentSSNChange = (val: string) => {
        if (!activeDependent) return
        const nums = val.replace(/\D/g, '').substring(0, 9)
        let formatted = nums
        if (nums.length > 5) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}-${nums.substring(5, 9)}`
        } else if (nums.length > 3) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}`
        }
        setActiveDependent({ ...activeDependent, ssn: formatted })
    }

    const handleAddDependentStart = () => {
        setActiveDependent({
            id: Date.now().toString(),
            firstName: '',
            lastName: '',
            dob: '',
            ssn: '',
            onPolicy: 'Yes'
        })
        setIsEditingDependent(true)
        setDependentFormErrors({})
    }

    const handleSaveDependent = () => {
        if (!activeDependent) return

        const newFormErrors: { [key: string]: string } = {}

        if (!activeDependent.firstName) newFormErrors.firstName = 'First name is required'
        if (!activeDependent.lastName) newFormErrors.lastName = 'Last name is required'
        if (!activeDependent.dob) {
            newFormErrors.dob = 'Date of birth is required'
        } else {
            const dependentAge = calculateAge(activeDependent.dob)
            if (dependentAge !== null) {
                if (dependentAge < 0) {
                    newFormErrors.dob = 'Too young: Date cannot be in the future'
                } else if (dependentAge > DEPENDENT_MAX_AGE) {
                    newFormErrors.dob = `Too old: Dependent must be ${DEPENDENT_MAX_AGE} years old or younger`
                }
            }
        }

        if (activeDependent.onPolicy === 'Yes') {
            if (!activeDependent.ssn) {
                newFormErrors.ssn = 'SSN is required when dependent is on policy'
            } else if (activeDependent.ssn.replace(/\D/g, '').length !== 9) {
                newFormErrors.ssn = 'SSN must be 9 digits'
            }
        }

        if (Object.keys(newFormErrors).length > 0) {
            setDependentFormErrors(newFormErrors)
            return
        }

        const newList = [...dependents, activeDependent]
        updateEnrollmentData('dependentsList', newList)
        setIsEditingDependent(false)
        setActiveDependent(null)
        setDependentFormErrors({})
    }

    const handleDeleteDependent = (id: string) => {
        const newList = dependents.filter(d => d.id !== id)
        updateEnrollmentData('dependentsList', newList)
    }

    const handleCancelDependentEdit = () => {
        setIsEditingDependent(false)
        setActiveDependent(null)
        setDependentFormErrors({})
    }

    // Auto-show dependent entry form when option 3 is selected
    useEffect(() => {
        if (hasDependents === 'Yes "I File Them On My Taxes"' && dependents.length === 0 && !isEditingDependent) {
            handleAddDependentStart()
        }
    }, [hasDependents])

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 1}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            onNext={handleNext}
            isNavigating={isNavigating}
        >
            <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Step 2: Enrollment Data</h3>
                        <p className="text-sm text-accent-600">Contact info, spouse, and dependents.</p>
                    </div>
                </div>

                {/* Master Mode: System Metadata & Transcription Monitor */}
                {isMasterMode && (
                    <div className="mb-8 p-5 bg-amber-50 border-2 border-amber-200 rounded-xl shadow-inner animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="bg-amber-100 p-1.5 rounded-md">
                                <svg className="w-4 h-4 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                </svg>
                            </div>
                            <h4 className="text-sm font-black text-amber-900 uppercase tracking-tighter">System Metadata & Transcription Monitor</h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                                <div>
                                    <p className="text-[10px] font-bold text-amber-600 uppercase">Session Audit Data</p>
                                    <div className="mt-1 space-y-2">
                                        <div className="p-2 bg-white rounded border border-amber-100 flex justify-between items-center">
                                            <span className="text-[10px] text-amber-700 font-bold uppercase">Session UUID</span>
                                            <span className="text-[11px] font-mono font-black text-indigo-700">{enrollmentData.sessionId || 'UNKNOWN'}</span>
                                        </div>
                                        <div className="p-2 bg-white rounded border border-amber-100 flex justify-between items-center">
                                            <span className="text-[10px] text-amber-700 font-bold uppercase">Campaign ID</span>
                                            <span className="text-[11px] font-mono font-black text-indigo-700">{enrollmentData.campaignId || 'DEFAULT'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-amber-100">
                                    <p className="text-[10px] font-bold text-amber-600 uppercase mb-2">Household Calculations</p>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-amber-700">Client Check:</span>
                                            <span className="font-bold text-amber-900">1</span>
                                        </div>
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-amber-700">Spouse On Policy:</span>
                                            <span className="font-bold text-amber-900">{maritalStatus === '3' ? '1' : '0'}</span>
                                        </div>
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-amber-700">Dependents Count:</span>
                                            <span className="font-bold text-amber-900">{dependents.filter(d => d.onPolicy === 'Yes').length}</span>
                                        </div>
                                        <div className="pt-1 mt-1 border-t border-amber-50 flex justify-between font-bold text-[11px]">
                                            <span className="text-amber-700 italic">Total Household Size:</span>
                                            <span className="text-blue-600 font-mono text-xs">{enrollmentData.householdSize || 1}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <p className="text-[10px] font-bold text-amber-600 uppercase">Transcription Markers</p>
                                    <div className="mt-1 p-3 bg-white rounded-lg border border-amber-100">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                            <span className="text-[10px] font-bold text-green-700 uppercase">Input: Real-time Transcription Active</span>
                                        </div>
                                        <p className="text-[10px] text-amber-800 leading-tight">
                                            Fields highlighted in <strong>amber-glow</strong> are currently being prioritized by the system transcription agent. Use the "Reveal All" toggle to see placement for silent transcription.
                                        </p>
                                    </div>
                                </div>
                                <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                                    <p className="text-[9px] font-bold text-indigo-600 uppercase mb-1 leading-none italic">Calibration Rule</p>
                                    <p className="text-[11px] text-indigo-700 leading-tight">
                                        FMOs can override <strong>State</strong> and <strong>Zip</strong> to test carrier availability across different regions without re-starting the session.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-8">
                    {/* SECTION: Contact Information */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Contact Information</h4>
                        <div className="space-y-4">
                            {/* Address */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Client Residential Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={enrollmentData.clientAddress || ''}
                                    onChange={(e) => updateEnrollmentData('clientAddress', e.target.value)}
                                    placeholder="Street Address"
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientAddress ? 'border-red-500' : 'border-gray-300'}`}
                                />
                                {errors.clientAddress && <p className="mt-1 text-sm text-red-600">{errors.clientAddress}</p>}
                                <input
                                    type="text"
                                    value={enrollmentData.clientAddressLine2 || ''}
                                    onChange={(e) => updateEnrollmentData('clientAddressLine2', e.target.value)}
                                    placeholder="Apartment, Suite, Unit, etc. (Optional)"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                                />
                            </div>

                            {/* City, State, Zip */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">City <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientCity || ''}
                                        onChange={(e) => updateEnrollmentData('clientCity', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientCity ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors.clientCity && <p className="mt-1 text-sm text-red-600">{errors.clientCity}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientState || ''}
                                        onChange={(e) => updateEnrollmentData('clientState', e.target.value)}
                                        readOnly={!isMasterMode}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-amber-50 border-amber-200'}`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientZip || ''}
                                        onChange={(e) => updateEnrollmentData('clientZip', e.target.value)}
                                        readOnly={!isMasterMode}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-amber-50 border-amber-200'}`}
                                    />
                                </div>
                            </div>

                            {/* Separate Mailing Address Question */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Does clients have different mailing address?</label>
                                <div className="flex space-x-6">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="No"
                                            checked={enrollmentData.hasSeparateMailingAddress !== 'Yes'}
                                            onChange={() => updateEnrollmentData('hasSeparateMailingAddress', 'No')}
                                            className="form-radio h-4 w-4 text-indigo-600"
                                        />
                                        <span className="ml-2 text-gray-700">No</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="Yes"
                                            checked={enrollmentData.hasSeparateMailingAddress === 'Yes'}
                                            onChange={() => updateEnrollmentData('hasSeparateMailingAddress', 'Yes')}
                                            className="form-radio h-4 w-4 text-indigo-600"
                                        />
                                        <span className="ml-2 text-gray-700">Yes</span>
                                    </label>
                                </div>
                            </div>

                            {/* Mailing Address Fields - Show if Yes OR if Reveal All is on */}
                            {(enrollmentData.hasSeparateMailingAddress === 'Yes' || (isMasterMode && revealAllFields)) && (
                                <div className="p-4 bg-gray-50 border border-gray-200 rounded-md space-y-4">
                                    <h5 className="text-sm font-semibold text-gray-800">Mailing Address</h5>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Street Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={enrollmentData.mailingAddress || ''}
                                            onChange={(e) => updateEnrollmentData('mailingAddress', e.target.value)}
                                            placeholder="Street Address"
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.mailingAddress ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        {errors.mailingAddress && <p className="mt-1 text-sm text-red-600">{errors.mailingAddress}</p>}
                                        <input
                                            type="text"
                                            value={enrollmentData.mailingAddressLine2 || ''}
                                            onChange={(e) => updateEnrollmentData('mailingAddressLine2', e.target.value)}
                                            placeholder="Apartment, Suite, Unit, etc. (Optional)"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">City <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={enrollmentData.mailingCity || ''}
                                                onChange={(e) => updateEnrollmentData('mailingCity', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.mailingCity ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.mailingCity && <p className="mt-1 text-sm text-red-600">{errors.mailingCity}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">State <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={enrollmentData.mailingState || ''}
                                                onChange={(e) => updateEnrollmentData('mailingState', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.mailingState ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.mailingState && <p className="mt-1 text-sm text-red-600">{errors.mailingState}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={enrollmentData.mailingZip || ''}
                                                onChange={(e) => updateEnrollmentData('mailingZip', e.target.value)}
                                                placeholder="12345"
                                                maxLength={10}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.mailingZip ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {showMailingZipError && (
                                                <p className="mt-1 text-sm text-red-600">
                                                    Please enter a valid zip code (e.g., 12345 or 12345-6789)
                                                </p>
                                            )}
                                            {errors.mailingZip && !enrollmentData.mailingZip && (
                                                <p className="mt-1 text-sm text-red-600">{errors.mailingZip}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Phone and Email */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Phone Number <span className="text-red-500">*</span></label>
                                    <input
                                        type="tel"
                                        value={enrollmentData.clientPhone || ''}
                                        onChange={(e) => handlePhoneChange(e.target.value)}
                                        placeholder="(555) 555-5555"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {showPhoneError && (
                                        <p className="mt-1 text-sm text-red-600">
                                            Phone number must be 10 digits ({phoneDigits.length}/10 entered)
                                        </p>
                                    )}
                                    {errors.clientPhone && !enrollmentData.clientPhone && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientPhone}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Email Address <span className="text-red-500">*</span></label>
                                    <input
                                        type="email"
                                        value={enrollmentData.clientEmail || ''}
                                        onChange={(e) => updateEnrollmentData('clientEmail', e.target.value)}
                                        placeholder="email@example.com"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {showEmailError && (
                                        <p className="mt-1 text-sm text-red-600">
                                            Please enter a valid email address (e.g., name@domain.com)
                                        </p>
                                    )}
                                    {errors.clientEmail && !enrollmentData.clientEmail && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientEmail}</p>
                                    )}
                                </div>
                            </div>

                            {/* Sex */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Client Sex <span className="text-red-500">*</span></label>
                                <select
                                    value={enrollmentData.clientSex || ''}
                                    onChange={(e) => updateEnrollmentData('clientSex', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientSex ? 'border-red-500' : 'border-gray-300'}`}
                                >
                                    <option value="">Select Sex</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                </select>
                                {errors.clientSex && <p className="mt-1 text-sm text-red-600">{errors.clientSex}</p>}
                            </div>

                            {/* Radio Groups - FMO Master View ONLY (Requirement: Hide Smoker/Pregnant for Agents) */}
                            {(isMasterMode && revealAllFields) && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Smoker <span className="text-red-500">*</span></label>
                                        <div className="flex space-x-6">
                                            <label className="flex items-center cursor-pointer">
                                                <input type="radio" value="Yes" checked={enrollmentData.isSmoker === 'Yes'} onChange={() => updateEnrollmentData('isSmoker', 'Yes')} className="form-radio h-4 w-4 text-indigo-600" />
                                                <span className="ml-2 text-gray-700">Yes</span>
                                            </label>
                                            <label className="flex items-center cursor-pointer">
                                                <input type="radio" value="No" checked={enrollmentData.isSmoker === 'No'} onChange={() => updateEnrollmentData('isSmoker', 'No')} className="form-radio h-4 w-4 text-indigo-600" />
                                                <span className="ml-2 text-gray-700">No</span>
                                            </label>
                                        </div>
                                        {errors.isSmoker && <p className="mt-1 text-sm text-red-600">{errors.isSmoker}</p>}
                                    </div>

                                    {/* Pregnancy Status - Show if Female OR if Reveal All is on */}
                                    {(enrollmentData.clientSex === 'Female' || (isMasterMode && revealAllFields)) && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Pregnant <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex space-x-6">
                                                <label className="flex items-center cursor-pointer">
                                                    <input type="radio" value="Yes" checked={enrollmentData.isPregnant === 'Yes'} onChange={() => updateEnrollmentData('isPregnant', 'Yes')} className="form-radio h-4 w-4 text-indigo-600" />
                                                    <span className="ml-2 text-gray-700">Yes</span>
                                                </label>
                                                <label className="flex items-center cursor-pointer">
                                                    <input type="radio" value="No" checked={enrollmentData.isPregnant === 'No'} onChange={() => updateEnrollmentData('isPregnant', 'No')} className="form-radio h-4 w-4 text-indigo-600" />
                                                    <span className="ml-2 text-gray-700">No</span>
                                                </label>
                                            </div>
                                            {errors.isPregnant && <p className="mt-1 text-sm text-red-600">{errors.isPregnant}</p>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SECTION: Spousal Data */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Spousal Information</h4>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Married <span className="text-red-500">*</span></label>
                                <div className="space-y-2">
                                    {MARITAL_OPTIONS.map(opt => (
                                        <div key={opt.value} className="flex items-center">
                                            <input
                                                type="radio"
                                                id={`married-${opt.value}`}
                                                name="maritalStatus"
                                                value={opt.value}
                                                checked={enrollmentData.maritalStatus === opt.value}
                                                onChange={(e) => updateEnrollmentData('maritalStatus', e.target.value)}
                                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            />
                                            <label htmlFor={`married-${opt.value}`} className="ml-3 block text-sm font-medium text-gray-700">
                                                {opt.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {errors.maritalStatus && <p className="mt-1 text-sm text-red-600">{errors.maritalStatus}</p>}
                            </div>

                            {/* Spouse Details - Show when Married File Joint (3) OR when Reveal All is on */}
                            {(maritalStatus === '3' || (isMasterMode && revealAllFields)) && (
                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                                    <h5 className="text-md font-semibold text-accent-800 mb-4">Spouse Details</h5>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Spouse First Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={enrollmentData.spouseFirstName || ''}
                                                    onChange={(e) => updateEnrollmentData('spouseFirstName', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.spouseFirstName ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {errors.spouseFirstName && <p className="mt-1 text-sm text-red-600">{errors.spouseFirstName}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Spouse Last Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={enrollmentData.spouseLastName || ''}
                                                    onChange={(e) => updateEnrollmentData('spouseLastName', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.spouseLastName ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {errors.spouseLastName && <p className="mt-1 text-sm text-red-600">{errors.spouseLastName}</p>}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Spouse Date of Birth <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={enrollmentData.spouseDOB || ''}
                                                    onChange={(e) => updateEnrollmentData('spouseDOB', e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.spouseDOB ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {showSpouseAgeError && spouseAge !== null && (
                                                    <p className="mt-1 text-sm text-red-600">
                                                        Age: {spouseAge} years old - NOT Eligible.
                                                        {spouseAge < minAge && ` Too young: Must be at least ${minAge} years old.`}
                                                        {spouseAge > maxAge && ` Too old: Must be ${maxAge} years old or younger.`}
                                                    </p>
                                                )}
                                                {errors.spouseDOB && !enrollmentData.spouseDOB && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.spouseDOB}</p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Spouse SSN <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={enrollmentData.spouseSSN || ''}
                                                    onChange={(e) => handleSpouseSSNChange(e.target.value)}
                                                    placeholder="###-##-####"
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.spouseSSN ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {errors.spouseSSN && <p className="mt-1 text-sm text-red-600">{errors.spouseSSN}</p>}
                                            </div>
                                        </div>
                                        <p className="text-sm text-blue-600">Household count has been increased by 1 for spouse.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SECTION: Dependents */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Dependents</h4>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Dependents <span className="text-red-500">*</span></label>
                                <div className="space-y-2">
                                    {['No', 'Yes "I Do Not Claim Them On My Taxes"', 'Yes "I File Them On My Taxes"'].map(opt => (
                                        <div key={opt} className="flex items-center">
                                            <input
                                                type="radio"
                                                id={`dep-${opt}`}
                                                name="hasDependents"
                                                value={opt}
                                                checked={enrollmentData.hasDependents === opt}
                                                onChange={(e) => updateEnrollmentData('hasDependents', e.target.value)}
                                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            />
                                            <label htmlFor={`dep-${opt}`} className="ml-3 block text-sm font-medium text-gray-700">
                                                {opt}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {errors.hasDependents && <p className="mt-1 text-sm text-red-600">{errors.hasDependents}</p>}
                            </div>

                            {/* Only show dependent form for option 3 OR if Reveal All is on */}
                            {(hasDependents === 'Yes "I File Them On My Taxes"' || (isMasterMode && revealAllFields)) && (
                                <div className="pt-4 border-t border-gray-200 space-y-4">
                                    {/* List of added dependents */}
                                    {dependents.length > 0 && (
                                        <div className="space-y-2">
                                            <h5 className="text-sm font-medium text-gray-700">Added Dependents ({dependents.length})</h5>
                                            {dependents.map((dep) => (
                                                <div key={dep.id} className="flex items-center justify-between bg-gray-50 p-4 rounded-md">
                                                    <div>
                                                        <p className="font-medium text-gray-900">{dep.firstName} {dep.lastName}</p>
                                                        <p className="text-sm text-gray-500">DOB: {dep.dob} | On Policy: {dep.onPolicy}</p>
                                                    </div>
                                                    <button onClick={() => handleDeleteDependent(dep.id)} className="text-red-600 hover:text-red-800 text-sm font-medium">Remove</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add/Edit Form - Forced in Reveal Mode */}
                                    {((isEditingDependent && activeDependent) || (isMasterMode && revealAllFields)) ? (
                                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                                            <h5 className="text-sm font-semibold text-gray-700 mb-4">
                                                {activeDependent ? 'Dependent Details' : 'Dependent Form'}
                                            </h5>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mb-4">
                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder="First Name*"
                                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${dependentFormErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                                                        value={activeDependent?.firstName || ''}
                                                        onChange={(e) => setActiveDependent({ ...(activeDependent || { id: 'temp', firstName: '', lastName: '', dob: '', ssn: '', onPolicy: 'No' }), firstName: e.target.value })}
                                                    />
                                                    {dependentFormErrors.firstName && <p className="mt-1 text-sm text-red-600">{dependentFormErrors.firstName}</p>}
                                                </div>
                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder="Last Name*"
                                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${dependentFormErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                                                        value={activeDependent?.lastName || ''}
                                                        onChange={(e) => setActiveDependent({ ...(activeDependent || { id: 'temp', firstName: '', lastName: '', dob: '', ssn: '', onPolicy: 'No' }), lastName: e.target.value })}
                                                    />
                                                    {dependentFormErrors.lastName && <p className="mt-1 text-sm text-red-600">{dependentFormErrors.lastName}</p>}
                                                </div>
                                                <div>
                                                    <input
                                                        type="date"
                                                        placeholder="Date of Birth*"
                                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${dependentFormErrors.dob || showDependentAgeError ? 'border-red-500' : 'border-gray-300'}`}
                                                        value={activeDependent?.dob || ''}
                                                        onChange={(e) => setActiveDependent({ ...(activeDependent || { id: 'temp', firstName: '', lastName: '', dob: '', ssn: '', onPolicy: 'No' }), dob: e.target.value })}
                                                        max={new Date().toISOString().split('T')[0]}
                                                    />
                                                    {showDependentAgeError && activeDependentAge !== null && (
                                                        <p className="mt-1 text-sm text-red-600">
                                                            Age: {activeDependentAge} years old -
                                                            {activeDependentAge < 0 ? ' Too young: Date cannot be in the future.' : ` Too old: Must be ${DEPENDENT_MAX_AGE} years old or younger.`}
                                                        </p>
                                                    )}
                                                    {dependentFormErrors.dob && <p className="mt-1 text-sm text-red-600">{dependentFormErrors.dob}</p>}
                                                </div>
                                                {activeDependent?.onPolicy === 'Yes' && (
                                                    <div>
                                                        <input
                                                            type="text"
                                                            placeholder="SSN*"
                                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${dependentFormErrors.ssn ? 'border-red-500' : 'border-gray-300'}`}
                                                            value={activeDependent?.ssn || ''}
                                                            onChange={(e) => handleDependentSSNChange(e.target.value)}
                                                        />
                                                        {dependentFormErrors.ssn && <p className="mt-1 text-sm text-red-600">{dependentFormErrors.ssn}</p>}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Will Dependent Be On Policy? <span className="text-red-500">*</span></label>
                                                <div className="flex space-x-4">
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            checked={activeDependent?.onPolicy === 'Yes'}
                                                            onChange={() => setActiveDependent(activeDependent ? { ...activeDependent, onPolicy: 'Yes', ssn: '' } : { id: 'temp', firstName: '', lastName: '', dob: '', ssn: '', onPolicy: 'Yes' })}
                                                            className="form-radio"
                                                        />
                                                        <span className="ml-2 text-sm">Yes</span>
                                                    </label>
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            checked={activeDependent?.onPolicy === 'No'}
                                                            onChange={() => setActiveDependent(activeDependent ? { ...activeDependent, onPolicy: 'No', ssn: '' } : { id: 'temp', firstName: '', lastName: '', dob: '', ssn: '', onPolicy: 'No' })}
                                                            className="form-radio"
                                                        />
                                                        <span className="ml-2 text-sm">No</span>
                                                    </label>
                                                </div>
                                                {activeDependent?.onPolicy === 'Yes' && (
                                                    <p className="text-sm text-blue-600 mt-2">This dependent will be added to the household count.</p>
                                                )}
                                            </div>
                                            <div className="flex justify-end space-x-3">
                                                <button onClick={handleCancelDependentEdit} className="text-gray-600 hover:text-gray-800 text-sm">Cancel</button>
                                                <button
                                                    onClick={handleSaveDependent}
                                                    className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm hover:bg-indigo-700"
                                                >
                                                    Save Dependent
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        dependents.length < 6 && (
                                            <button
                                                onClick={handleAddDependentStart}
                                                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors flex items-center justify-center font-medium"
                                            >
                                                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Add Dependent
                                            </button>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Household Size Summary */}
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                        <div className="flex items-center justify-between">
                            <span className="font-semibold text-indigo-800">Total Household Size:</span>
                            <span className="text-2xl font-bold text-indigo-600">{enrollmentData.householdSize || 1}</span>
                        </div>
                        <p className="text-sm text-indigo-600 mt-1">
                            Client (1) {maritalStatus === '3' ? '+ Spouse (1)' : ''} {dependents.filter(d => d.onPolicy === 'Yes').length > 0 ? `+ Dependents (${dependents.filter(d => d.onPolicy === 'Yes').length})` : ''}
                        </p>
                    </div>
                </div>
            </div>
        </StageWrapper>
    )
}
